/**
 * Custom hook for managing OTP input state and interactions
 */
import { useState, useRef, KeyboardEvent, ClipboardEvent } from "react"

const OTP_LENGTH = 6

interface UseOTPInputReturn {
  otpValues: string[]
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>
  handleChange: (index: number, value: string) => void
  handleKeyDown: (index: number, e: KeyboardEvent<HTMLInputElement>) => void
  handlePaste: (e: ClipboardEvent<HTMLInputElement>) => void
  getOtpString: () => string
  resetOTP: () => void
}

export const useOTPInput = (): UseOTPInputReturn => {
  const [otpValues, setOtpValues] = useState<string[]>(Array(OTP_LENGTH).fill(""))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus()
  }

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newOtpValues = [...otpValues]
    newOtpValues[index] = value.slice(-1) // Only take the last character
    setOtpValues(newOtpValues)

    // Move to next input if value entered and next input exists
    if (value && index < OTP_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        focusInput(index - 1)
      }
      const newOtpValues = [...otpValues]
      newOtpValues[index] = ""
      setOtpValues(newOtpValues)
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1)
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData("text").trim()
    
    if (!/^\d+$/.test(pastedData)) return

    const pastedArray = pastedData.slice(0, OTP_LENGTH).split("")
    const newOtpValues = [...otpValues]
    
    pastedArray.forEach((value, index) => {
      if (index < OTP_LENGTH) {
        newOtpValues[index] = value
      }
    })
    
    setOtpValues(newOtpValues)

    const nextIndex = Math.min(pastedArray.length, OTP_LENGTH - 1)
    focusInput(nextIndex)
  }

  const getOtpString = () => otpValues.join("")

  const resetOTP = () => {
    setOtpValues(Array(OTP_LENGTH).fill(""))
  }

  return {
    otpValues,
    inputRefs,
    handleChange,
    handleKeyDown,
    handlePaste,
    getOtpString,
    resetOTP,
  }
}

